import { Outlet, useParams } from 'react-router-dom';
import { SwitcherModal } from '@kinesis/spotlight-ui';
import { useSessionQuery, useLogoutMutation } from '@/api/sessions';
import { useFlag } from '@/hooks/use-flag';
import { useIsEditor } from '@/hooks/use-is-editor';
import { AppLayout } from '@/components/app-layout';
import { useDispatch, useSelector } from 'react-redux';
import { flagsSelector, toggleFlag, useGetFlagsQuery } from '@/api/flags';
import {
  useRecentEntitiesQuery,
  useSearchEntitiesQuery,
  useEntityTypesQuery,
  getEntityUrl,
  getEntityTypeUrl,
} from '@/api/entities';
import { useCallback, useState } from 'react';
import { isEmpty, isNil } from 'lodash/fp';
import { Toaster, Header, Frame } from '@kinesis/bungle';
import { InventoryToolbar } from '@/components/inventory-toolbar';
import { ToolbarPathways } from '@/pages/toolbar-pathways';
import { ToolbarScenario } from '@/pages/toolbar-scenario';
import { ToolbarGpc } from '@/pages/toolbar-gpc';
import { ToolbarDisclosures } from '@/pages/toolbar-disclosures';
import { useGetInventoryQuery } from '@/api/inventories';
import { useNavSelection } from '@/hooks/use-nav-selection';
import Highcharts from 'highcharts';
import NoDataToDisplay from 'highcharts/modules/no-data-to-display';

const getCurrentNav = (navSelection: 'pathways') =>
  ({
    pathways: 'Pathways',
  })[navSelection];

const getCurrentNavLink = (navSelection: 'pathways') =>
  ({
    pathways: 'pathways',
  })[navSelection];

type InventoryUrl = {
  inventoryId: string;
  scenarioId?: string;
  disclosureId?: string;
};

const App = () => {
  NoDataToDisplay(Highcharts);
  const dispatch = useDispatch();
  const { data: session } = useSessionQuery();
  const [logout] = useLogoutMutation();
  const { isSuccess: flagsFetched } = useGetFlagsQuery();
  const enableFlags = useFlag('app.flag-management');
  const enableBaseline = useFlag('community.baseline');
  const enableGpc = useFlag('community.gpc');
  const enableDisclosure = useFlag('community.disclosure');
  const flags = useSelector(flagsSelector);
  const [entitySearch, setEntitySearch] = useState('');
  const { data: searchResponse } = useSearchEntitiesQuery(entitySearch, {
    skip: isEmpty(entitySearch),
  });
  const { data: entityTypesResponse } = useEntityTypesQuery(undefined, {
    skip: !flagsFetched,
  });
  const { data: recentEntitiesResponse } = useRecentEntitiesQuery(undefined, {
    skip: !flagsFetched,
  });
  const [switcherModal, setSwitcherModal] = useState(false);
  const onOpenSwitcherModal = useCallback(() => {
    setSwitcherModal(true);
  }, [setSwitcherModal]);

  const onCloseSwitcherModal = useCallback(() => {
    setSwitcherModal(false);
    setEntitySearch('');
  }, [setEntitySearch, setSwitcherModal]);

  const onToggleFlag = useCallback(
    (name: string) => {
      dispatch(toggleFlag(name) as any);
    },
    [dispatch],
  );

  const searchEntities = isEmpty(entitySearch) ? [] : searchResponse?.results;
  const recentEntities = recentEntitiesResponse || [];
  const entityTypes = entityTypesResponse || [];

  const navSelection = useNavSelection();
  const params = useParams<InventoryUrl>();
  const inventoryId = Number(params.inventoryId);
  const scenarioId = params && params.scenarioId && Number(params.scenarioId);
  const disclosureId =
    params && params.disclosureId && Number(params.disclosureId);

  const isEditor = useIsEditor();
  const { data: inventory } = useGetInventoryQuery(inventoryId);

  return (
    <AppLayout
      inventoryId={inventoryId}
      flags={flags}
      session={session}
      enableFlags={enableFlags}
      enableBaseline={enableBaseline}
      enableGpc={enableGpc}
      enableDisclosure={enableDisclosure}
      onToggleFlag={onToggleFlag}
      onLogout={logout}
      onOpenSwitcher={onOpenSwitcherModal}
      inventory={inventory}
    >
      <>
        <Header border shadow>
          <InventoryToolbar
            isEditor={isEditor}
            currentNav={getCurrentNav(navSelection)}
            currentNavLink={getCurrentNavLink(navSelection)}
            breadcrumbTail={
              navSelection === 'pathways' &&
              !isNil(scenarioId) && <ToolbarScenario />
            }
          >
            {navSelection === 'pathways' && isNil(scenarioId) && (
              <ToolbarPathways />
            )}
            {navSelection === 'gpc' && <ToolbarGpc />}
            {navSelection === 'disclosures' && isNil(disclosureId) && (
              <ToolbarDisclosures />
            )}
          </InventoryToolbar>
          <Toaster id='globalTop' placement='top' />
        </Header>
        <Frame>
          <Outlet />
        </Frame>
        {switcherModal && (
          <SwitcherModal
            width={496}
            height={600}
            minHeight={320}
            onClose={onCloseSwitcherModal}
            onSearch={setEntitySearch}
            entityTypeList={entityTypes}
            searchResultList={searchEntities}
            recentList={recentEntities}
            getEntityUrl={getEntityUrl}
            getEntityTypeUrl={getEntityTypeUrl}
          />
        )}
      </>
    </AppLayout>
  );
};

export { App };
